import React from 'react';
import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  FileText,
  ShieldCheck,
  Users,
  History,
  Cpu,
  Sliders,
  Shield,
  CreditCard,
  X,
} from 'lucide-react';

import { useAuth } from '../context/AuthContext';

interface SidebarProps {
  isOpen?: boolean;
  onClose?: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
  const { isSuperAdmin } = useAuth();

  const superAdminNavItems = [
    { to: '/overview', label: 'Platform Overview', icon: LayoutDashboard },
    { to: '/dashboard', label: 'All Jobs & Workspace', icon: FileText },
    { to: '/jobs-by-user', label: 'Jobs by User', icon: FileText },
    { to: '/providers', label: 'AI Provider Management', icon: Cpu },
    { to: '/users', label: 'Users & Access', icon: Users },
    { to: '/audit', label: 'Audit Logs', icon: History },
    { to: '/pii-rules', label: 'PII & Redaction', icon: ShieldCheck },
    { to: '/validation-rules', label: 'Validation Engine', icon: Sliders },
    { to: '/policies', label: 'Policies', icon: Shield },
    { to: '/subscription', label: 'Subscription & Plans', icon: CreditCard },
  ];

  const userNavItems = [
    { to: '/dashboard', label: 'My Jobs & Documents', icon: FileText },
    { to: '/providers', label: 'Active AI Models', icon: Cpu },
    { to: '/subscription', label: 'Subscription & Plans', icon: CreditCard },
  ];

  const navItems = isSuperAdmin ? superAdminNavItems : userNavItems;

  return (
    <>
      {/* Mobile Backdrop Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs z-40 md:hidden transition-opacity"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      {/* Sidebar container */}
      <aside
        className={`
          fixed md:sticky top-0 md:top-16 left-0 z-50 md:z-auto
          h-screen md:h-[calc(100vh-4rem)] w-64 md:w-60
          bg-white border-r border-slate-200 flex flex-col justify-between py-5 px-3
          transition-transform duration-300 ease-in-out
          ${isOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
          shadow-2xl md:shadow-none
        `}
      >
        <div className="space-y-1">
          <div className="flex items-center justify-between px-3 py-2">
            <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">
              Navigation
            </span>
            <button
              onClick={onClose}
              className="md:hidden p-1 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100"
              aria-label="Close menu"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={onClose}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-xl font-medium text-xs transition-all ${
                    isActive
                      ? 'bg-sky-50 text-sky-700 font-semibold border border-sky-100 shadow-sm'
                      : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                  }`
                }
              >
                <Icon className="w-4 h-4 shrink-0" />
                <span>{item.label}</span>
              </NavLink>
            );
          })}
        </div>

        <div className="px-3 py-3 rounded-xl bg-slate-50 border border-slate-200 text-xs space-y-1 mt-6">
          <div className="flex items-center gap-2 font-semibold text-slate-800">
            <ShieldCheck className="w-4 h-4 text-sky-600" />
            <span>Privacy Guaranteed</span>
          </div>
          <p className="text-[11px] text-slate-500 leading-relaxed">
            Zero raw PII storage across all jurisdictions.
          </p>
        </div>
      </aside>
    </>
  );
};


